import { useEffect, useMemo, useRef, useState } from "react";
import { FileDropZone } from "@/components/common/FileDropZone";
import { useApolloClient, useMutation } from "@apollo/client/react";
import { FormattedMessage, useIntl } from "react-intl";
import { userErrorMessage } from "@/lib/user-error";
import { Button } from "@/components/design/Button";
import { Pager } from "@/components/design/Pager";
import {
  CreateDocumentDocument,
  MyselfExpertiseDataDocument,
  PowersDataDocument,
  StartDocumentsExtractionDocument,
} from "@/graphql/generated/operations";
import { awaitDocumentsExtraction } from "@/lib/await-extraction";
import { throwForValidationResult, userFacingGraphqlError } from "@/lib/graphql-error";
import { elementDisplayName, elementNameId } from "@/lib/elements";
import { levelFromPercent } from "@/lib/skill-levels";
import { MAX_DOCUMENT_UPLOAD_BYTES } from "@/lib/upload-limits";
import { ANALYZABLE_ACCEPT, isAnalyzableDocument } from "@/lib/analyzable-documents";

// genesis startDocumentsExtraction runs LLM extraction (Document Extraction
// service) over documents and writes the results onto the person's profile.
// This dialog covers the two silently-persisting pipelines — Power- (extractPowers)
// and Hardskill-/Fachwissen- (extractSkills) analysis — parameterised by `flags`.
// CV/profile analysis is a review-first flow and lives in ProfileExtractionDialog
// (the extractProfileFromDocument mutation), NOT here — genesis does not process a
// profile extraction through this path.
//
// Transport (genesis #113): the mutation returns immediately with a QUEUED job;
// progress and the final result stream over the documentsExtractionEvents
// subscription. This replaces the old blocking processDocuments await, which
// the BFF severed at GENESIS_TIMEOUT_MS (~30s) well before a minutes-long power
// extraction finished. On a dropped socket the outcome is recovered by polling
// the job status, with the completion notification as the durable fallback.

export interface ExtractionFlags {
  extractPowers?: boolean;
  extractSkills?: boolean;
}

export interface ExtractionDoc {
  id: string;
  name: string;
  // Folder the document lives in (null = "Ohne Ordner").
  groupId?: string | null;
  // Drives the newest-first order of the list.
  createdAt?: string | null;
}

interface PendingFile {
  fileName: string;
  mimeType: string;
  content: string; // base64 (no data-URL prefix)
}

interface PipelineResult {
  status: string;
  processed: number;
  skipped: number;
  errored: number;
}

interface ProcessResult {
  status: string;
  message?: string | null;
  warnings?: readonly string[] | null;
  documents: readonly { ref: string; status: string; message?: string | null; displayName?: string }[];
  powers?: PipelineResult | null;
  skills?: PipelineResult | null;
}

function readBase64(file: File): Promise<PendingFile> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const s = String(reader.result);
      const comma = s.indexOf(",");
      resolve({
        fileName: file.name,
        mimeType: file.type || "application/octet-stream",
        content: comma >= 0 ? s.slice(comma + 1) : s,
      });
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

// Rows per page in the "From Documents" list, as on the Documents page.
const DOCS_PER_PAGE = 24;

export function ExtractionDialog({
  title,
  flags,
  docs,
  groups = [],
  docsLoading = false,
  personId,
  onClose,
  onDone,
}: {
  title: string;
  flags: ExtractionFlags;
  docs: readonly ExtractionDoc[];
  groups?: readonly { id: string; name: string }[];
  // While true the stock is still arriving, so an empty list says "not yet".
  docsLoading?: boolean;
  // Needed to persist newly uploaded files as Documents before analysing them.
  personId: string | null;
  onClose: () => void;
  onDone?: () => void;
}) {
  const intl = useIntl();
  const apollo = useApolloClient();
  const [startDocumentsExtraction] = useMutation(StartDocumentsExtractionDocument);
  const [createDocument] = useMutation(CreateDocumentDocument);
  const [pending, setPending] = useState<PendingFile[]>([]);
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());
  const [docQuery, setDocQuery] = useState("");
  const [docPage, setDocPage] = useState(0);
  const [folderPages, setFolderPages] = useState<Record<string, number>>({});
  const [busy, setBusy] = useState(false);
  // True once the background job has been accepted (a job id came back). From
  // that point the extraction is detached server-side, so the user may close the
  // dialog and keep working — the completion notification is the durable signal.
  const [jobAccepted, setJobAccepted] = useState(false);
  // Aborts the background event watch when the dialog is closed mid-run, so we
  // don't leave a dangling subscription/poll after the user walks away.
  const abortRef = useRef<AbortController | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ProcessResult | null>(null);
  // Delta workaround: processDocuments returns only counts, not which entries
  // were added. We snapshot Fachwissen (expertises) before/after and show the
  // newly-added ones. null = not computed (e.g. powers analysis or lookup failed).
  const [newSkills, setNewSkills] = useState<{ skill: string; level: string | null }[] | null>(
    null,
  );
  // Powers delta: the extraction fills `smartAnalysisResults` (per power
  // element, with a score). We show elements that are new or whose score
  // changed compared to before the run.
  const [newPowers, setNewPowers] = useState<{ element: string; score: number }[] | null>(null);
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  // Whether freshly uploaded files are persisted as Documents. Off → the file
  // is only sent for analysis (ephemeral, not stored).
  const [persistUploads, setPersistUploads] = useState(true);

  // Stop watching the job if the dialog is unmounted (user closed it mid-run).
  useEffect(() => () => abortRef.current?.abort(), []);

  // Names already present in Documents (case-insensitive) - used to warn about
  // duplicates the user may have overlooked, and to reuse the existing doc.
  const existingNames = new Set(docs.map((d) => d.name.toLowerCase()));

  const toggleCollapse = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const total = pending.length + selectedIds.size;

  async function addFiles(files: FileList | null) {
    if (!files) return;
    setError(null);
    const accepted: PendingFile[] = [];
    for (const f of Array.from(files)) {
      if (!isAnalyzableDocument(f.name, f.type)) {
        setError(
          intl.formatMessage(
            {
              id: "save.extraction.unsupportedType",
              defaultMessage:
                "“{name}” can’t be analysed. Supported: PDF, Word, Excel and images.",
            },
            { name: f.name },
          ),
        );
        continue;
      }
      if (f.size > MAX_DOCUMENT_UPLOAD_BYTES) {
        setError(
          intl.formatMessage(
            {
              id: "save.extraction.fileTooLarge",
              defaultMessage: "“{name}” is too large (max. 10 MB per file).",
            },
            { name: f.name },
          ),
        );
        continue;
      }
      accepted.push(await readBase64(f));
    }
    if (accepted.length) setPending((p) => [...p, ...accepted]);
  }

  const toggleDoc = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleMany = (ids: readonly string[], select: boolean) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const id of ids) {
        if (select) next.add(id);
        else next.delete(id);
      }
      return next;
    });

  // Fachwissen entries: the displayable text is `description` (`position` is a
  // numeric sort order, `level` the proficiency stored as a percent).
  async function fetchExpertises(): Promise<
    { id: string; description: string | null; level: number | null }[]
  > {
    const r = await apollo.query({
      query: MyselfExpertiseDataDocument,
      fetchPolicy: "network-only",
    });
    return (r.data?.myself?.expertises ?? []).map((e) => ({
      id: e.id,
      description: e.description ?? null,
      level: e.level ?? null,
    }));
  }

  // Smart-analysis (power) results the extraction service fills, keyed by the
  // power element with a score.
  async function fetchSmartAnalysis(): Promise<{ element: string; score: number }[]> {
    const r = await apollo.query({ query: PowersDataDocument, fetchPolicy: "network-only" });
    return (r.data?.smartAnalysisResults ?? []).map((s) => ({
      element: String(s.element),
      score: s.score,
    }));
  }

  // Group the "Aus Documents" list by folder (mirrors the Documents view) so
  // it's clear which documents live where; loose ones trail under "Ohne Ordner".
  // A filter drops the folders and searches flat: among hundreds of documents,
  // paging to the right folder is not finding.
  const loadingLine = (
    <div className="text-[12px] text-ink/40">
      <FormattedMessage
        id="save.extraction.loadingDocuments"
        defaultMessage="Loading documents …"
      />
    </div>
  );
  const searchLabel = intl.formatMessage({
    id: "save.extraction.searchDocuments",
    defaultMessage: "Search documents",
  });
  // Only what can actually be analysed - a picker whose sole purpose is picking
  // something to analyse has no business listing an archive. Newest first,
  // mirroring the Documents page: whoever just uploaded a file opens the
  // analysis to use it, and must not have to page to it.
  const ordered = useMemo(
    () =>
      docs
        .filter((d) => isAnalyzableDocument(d.name))
        .sort((a, b) => String(b.createdAt ?? "").localeCompare(String(a.createdAt ?? ""))),
    [docs],
  );
  const filter = docQuery.trim().toLowerCase();
  const grouped = filter
    ? []
    : groups
        .map((g) => {
          const inFolder = ordered.filter((d) => d.groupId === g.id);
          const totalPages = Math.max(1, Math.ceil(inFolder.length / DOCS_PER_PAGE));
          const page = Math.min(folderPages[g.id] ?? 0, totalPages - 1);
          return {
            id: g.id,
            name: g.name,
            docs: inFolder,
            totalPages,
            page,
            shown: inFolder.slice(page * DOCS_PER_PAGE, page * DOCS_PER_PAGE + DOCS_PER_PAGE),
          };
        })
        .filter((g) => g.docs.length > 0);
  // A document whose folder is unknown (the folder query resolves separately and
  // may fail) trails with the loose ones rather than disappearing.
  const knownGroups = new Set(groups.map((g) => g.id));
  const visibleDocs = filter
    ? ordered.filter((d) => d.name.toLowerCase().includes(filter))
    : ordered.filter((d) => !d.groupId || !knownGroups.has(d.groupId));
  const docTotalPages = Math.max(1, Math.ceil(visibleDocs.length / DOCS_PER_PAGE));
  // Clamped, so a document deleted or uploaded under the reader can't strand
  // them on a page that no longer exists.
  const docPageClamped = Math.min(docPage, docTotalPages - 1);
  const pagedDocs = visibleDocs.slice(
    docPageClamped * DOCS_PER_PAGE,
    docPageClamped * DOCS_PER_PAGE + DOCS_PER_PAGE,
  );

  const docRow = (d: ExtractionDoc) => {
    return (
      <label
        key={d.id}
        className="box-flat flex cursor-pointer items-center gap-2.5 bg-page px-3 py-2 text-[12px] hover:bg-ink/[0.06]"
      >
        <input
          type="checkbox"
          checked={selectedIds.has(d.id)}
          onChange={() => toggleDoc(d.id)}
          className="h-4 w-4 shrink-0 accent-ink"
        />
        <span className="truncate">{d.name}</span>
      </label>
    );
  };

  async function run() {
    if (busy || total === 0) return;
    setBusy(true);
    setError(null);
    setJobAccepted(false);
    setNewSkills(null);
    setNewPowers(null);
    try {
      // Snapshot Fachwissen / Powers before analysis so we can show what got
      // added (processDocuments only returns counts). Best-effort: never block.
      let beforeSkillIds: Set<string> | null = null;
      if (flags.extractSkills) {
        try {
          beforeSkillIds = new Set((await fetchExpertises()).map((e) => e.id));
        } catch {
          beforeSkillIds = null;
        }
      }
      let beforePowers: Map<string, number> | null = null;
      if (flags.extractPowers) {
        try {
          beforePowers = new Map((await fetchSmartAnalysis()).map((s) => [s.element, s.score]));
        } catch {
          beforePowers = null;
        }
      }
      const documents: { fileName: string; documentId?: string; content?: string }[] = [];
      for (const f of pending) {
        // Reuse rather than re-upload: a file whose name is already in Documents is analysed from
        // the stored document instead. This is a deliberate choice to avoid uploading the same bytes
        // twice, NOT a consequence of a uniqueness rule - genesis has no unique constraint on
        // document_name, so it is a heuristic: with several same-named documents this picks the
        // first match arbitrarily.
        const existing = docs.find((d) => d.name.toLowerCase() === f.fileName.toLowerCase());
        if (existing) {
          documents.push({ fileName: existing.name, documentId: existing.id });
          continue;
        }
        if (!persistUploads) {
          // Analyse-only: send the content inline, don't store a Document.
          // Inline (ephemeral) analyses yield scores but no stored evidence -
          // genesis has no document to attach the quotes to.
          documents.push({ fileName: f.fileName, content: f.content });
          continue;
        }
        // Persist as a new Document (normal upload → no "Lebenslauf" tag →
        // source "Dateiupload"), then analyse it by id so evidence rows can
        // attach to the document and re-runs are deduplicated.
        if (!personId)
          throw new Error(
            intl.formatMessage({
              id: "save.extraction.notSignedIn",
              defaultMessage: "Not signed in - upload not possible.",
            }),
          );
        const cr = await createDocument({
          variables: {
            input: {
              data: {
                name: f.fileName,
                person: { id: personId },
                tags: [],
                // fileName is a top-level Document field; CreateFileDataInput
                // (content) only accepts mimeType + content.
                fileName: f.fileName,
                content: { mimeType: f.mimeType, content: f.content },
              },
              dryRun: false,
            },
          },
        }).catch((e: unknown) => {
          throw new Error(
            userFacingGraphqlError(
              e,
              intl.formatMessage({
                id: "save.extraction.uploadFailedDuplicate",
                defaultMessage:
                  "Upload failed. A document with this name probably already exists.",
              }),
            ),
          );
        });
        const cout = cr.data?.createDocument;
        if (cout?.__typename !== "CreateDocumentResult") {
          throwForValidationResult(
            cout,
            intl.formatMessage({
              id: "save.extraction.uploadFailedDuplicate",
              defaultMessage:
                "Upload failed. A document with this name probably already exists.",
            }),
          );
        }
        documents.push({
          fileName: cout.result.fileName ?? cout.result.name ?? f.fileName,
          documentId: cout.result.id,
        });
      }
      for (const d of docs.filter((d) => selectedIds.has(d.id))) {
        documents.push({ fileName: d.name, documentId: d.id });
      }
      // The same document can arrive twice (a dropped file that already
      // existed + its checkbox selection); send each one once.
      const seen = new Set<string>();
      const uniqueDocuments = documents.filter((d) => {
        const key = d.documentId ?? d.fileName.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      // Fire the job: returns immediately with a QUEUED handle. The blocking
      // wait now lives on the subscription, not the HTTP request.
      const started = await startDocumentsExtraction({
        variables: {
          input: {
            documents: uniqueDocuments,
            extractPowers: Boolean(flags.extractPowers),
            // Required by the schema but not implemented on this path; profile
            // extraction is the separate extractProfileFromDocument flow.
            extractProfile: false,
            extractSkills: Boolean(flags.extractSkills),
          },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any,
      }).catch((e: unknown) => {
        throw new Error(
          intl.formatMessage(
            {
              id: "save.extraction.analysisFailedWithReason",
              defaultMessage: "Analysis failed ({reason}).",
            },
            { reason: userErrorMessage(intl, e) },
          ),
          { cause: e },
        );
      });
      const jobId = started.data?.startDocumentsExtraction?.id;
      if (!jobId) {
        throw new Error(
          intl.formatMessage({
            id: "save.extraction.analysisFailed",
            defaultMessage: "Analysis failed.",
          }),
        );
      }

      // Job accepted and now detached server-side: from here the user is free to
      // close the dialog and keep working (Close is enabled below).
      setJobAccepted(true);

      // Watch the job's events for the live result (status poll is the
      // dropped-socket fallback inside awaitDocumentsExtraction). Aborted if the
      // dialog is closed mid-run.
      const controller = new AbortController();
      abortRef.current = controller;
      const outcome = await awaitDocumentsExtraction(apollo, jobId, { signal: controller.signal });

      // Dialog was closed while the job ran — nothing to render.
      if (outcome.kind === "cancelled") {
        return;
      }

      if (outcome.kind === "timeout") {
        // The job is still running server-side; its result arrives as a durable
        // notification that deep-links back here.
        throw new Error(
          intl.formatMessage({
            id: "save.extraction.stillRunning",
            defaultMessage:
              "The analysis is taking a while and keeps running in the background. You’ll get a notification when it’s done.",
          }),
        );
      }

      const out = outcome.result;
      if (outcome.kind === "failed" && !out) {
        throw new Error(
          outcome.message ??
            intl.formatMessage({
              id: "save.extraction.analysisFailed",
              defaultMessage: "Analysis failed.",
            }),
        );
      }

      // On the poll-recovered path the result payload is absent; synthesise a
      // minimal result so the outcome (and the computed deltas below) still show.
      const resolved: ProcessResult = out
        ? {
            ...(out as unknown as ProcessResult),
            documents: out.documents.map((d, index) => ({
              ...d,
              displayName: uniqueDocuments[index]?.fileName,
            })),
          }
        : {
            status: outcome.kind === "completed" ? "success" : "error",
            message: outcome.message ?? null,
            warnings: null,
            documents: [],
          };
      setResult(resolved);
      // Compute the Fachwissen delta (new entries, with proficiency) for display.
      if (flags.extractSkills && beforeSkillIds) {
        try {
          const after = await fetchExpertises();
          setNewSkills(
            after
              .filter((e) => !beforeSkillIds.has(e.id))
              .map((e) => ({
                skill:
                  e.description ??
                  intl.formatMessage({
                    id: "save.extraction.expertiseFallback",
                    defaultMessage: "Expertise",
                  }),
                level: (() => {
                  const level = levelFromPercent(e.level);
                  return level ? intl.formatMessage({ id: level.nameId, defaultMessage: level.name }) : null;
                })(),
              })),
          );
        } catch {
          // delta is a nice-to-have; ignore lookup failures
        }
      }
      // Compute the Powers delta (elements newly scored or whose score changed).
      if (flags.extractPowers && beforePowers) {
        try {
          const after = await fetchSmartAnalysis();
          setNewPowers(
            after.filter((s) => beforePowers.get(s.element) !== s.score),
          );
        } catch {
          // delta is a nice-to-have; ignore lookup failures
        }
      }
      onDone?.();
    } catch (e) {
      // Errors wrapped by the steps above (cause set) carry labeled product
      // copy with an already-sanitized reason; anything else is raw and goes
      // through the @adr SPARK-0010-no-raw-server-errors-in-ui@1 mapper.
      setError(
        e instanceof Error && e.cause !== undefined ? e.message : userErrorMessage(intl, e),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[1000] flex bg-ink/30 p-4 backdrop-blur-[1px]"
      data-graphql="StartDocumentsExtraction, DocumentsExtractionEvents, CreateDocument, MyselfExpertises, PowersData"
    >
      <div className="m-auto flex max-h-full w-full max-w-[640px] flex-col overflow-hidden rounded-md bg-page shadow-xl ring-1 ring-ink/10">
        <div className="flex items-center justify-between gap-3 border-b border-ink/10 px-5 py-3">
          <div className="min-w-0">
            <div className="field-label text-ink/45">
              <FormattedMessage
                id="save.extraction.poweredBy"
                defaultMessage="Powered by smycles AI"
              />
            </div>
            <div className="font-serif text-2xl leading-tight">{title}</div>
          </div>
          <Button
            variant="secondary"
            onClick={onClose}
            // Locked only during the brief upload/start phase; once the job is
            // accepted it runs detached, so closing is safe (and encouraged).
            disabled={busy && !jobAccepted}
            className="px-3 py-1 text-[10px] disabled:opacity-40"
          >
            <FormattedMessage id="common.close" defaultMessage="Close" />
          </Button>
        </div>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-5">
          {result ? (
            <ResultView
              result={result}
              flags={flags}
              docs={docs}
              newSkills={newSkills}
              newPowers={newPowers}
            />
          ) : (
            <>
              <div>
                <div className="field-label mb-1.5 text-ink/45">
                  <FormattedMessage id="save.extraction.newFile" defaultMessage="New file" />
                </div>
                <FileDropZone
                  variant="zone"
                  clickToOpen
                  accept={ANALYZABLE_ACCEPT}
                  onFiles={(fl) => void addFiles(fl)}
                  label={
                    <FormattedMessage
                      id="save.extraction.dropZone"
                      defaultMessage="Drag a file here or click to upload"
                    />
                  }
                  hint={
                    <FormattedMessage
                      id="save.extraction.dropZoneHint"
                      defaultMessage="PDF, Word, Excel, images · max. 10 MB per file"
                    />
                  }
                />

                <div className="mt-3 flex items-center justify-between gap-3">
                  <span className="text-[11px] text-ink/55">
                    <FormattedMessage
                      id="save.extraction.persistUploads"
                      defaultMessage="Save uploaded files in Documents"
                    />
                  </span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={persistUploads}
                    aria-label={intl.formatMessage({
                      id: "save.extraction.persistUploads",
                      defaultMessage: "Save uploaded files in Documents",
                    })}
                    onClick={() => setPersistUploads((v) => !v)}
                    className={`inline-flex h-5 w-9 shrink-0 items-center rounded-full transition ${
                      persistUploads ? "bg-accent" : "bg-ink/25"
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-surface shadow transition ${
                        persistUploads ? "translate-x-[18px]" : "translate-x-0.5"
                      }`}
                    />
                  </button>
                </div>

                {pending.length > 0 ? (
                  <div className="mt-2 space-y-1">
                    {pending.map((f, i) => {
                      const duplicate = existingNames.has(f.fileName.toLowerCase());
                      return (
                        <div
                          key={`${f.fileName}-${i}`}
                          className="box-flat bg-page px-3 py-1.5 text-[12px]"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="truncate">{f.fileName}</span>
                            <button
                              type="button"
                              onClick={() => setPending((p) => p.filter((_, j) => j !== i))}
                              aria-label={intl.formatMessage({
                                id: "common.remove",
                                defaultMessage: "Remove",
                              })}
                              className="shrink-0 text-ink/40 hover:text-red-600"
                            >
                              ×{/* i18n-allow: close glyph, not copy */}
                            </button>
                          </div>
                          {duplicate ? (
                            <div className="mt-0.5 text-[10px] text-amber-700">
                              <FormattedMessage
                                id="save.extraction.duplicateWarning"
                                defaultMessage="Already exists in Documents - the stored document is analysed, no re-upload."
                              />
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </div>

              <div>
                <div className="field-label mb-1.5 text-ink/45">
                  <FormattedMessage
                    id="save.extraction.fromDocuments"
                    defaultMessage="From Documents"
                  />
                </div>
                {ordered.length > 0 ? (
                  <div className="box-flat mb-2 flex items-center bg-page px-3 py-1.5">
                    <input
                      type="search"
                      value={docQuery}
                      onChange={(e) => {
                        setDocQuery(e.target.value);
                        setDocPage(0);
                      }}
                      aria-label={searchLabel}
                      placeholder={searchLabel}
                      className="flex-1 bg-transparent text-[12px] placeholder:text-ink/40 focus:outline-none"
                    />
                  </div>
                ) : null}
                {ordered.length === 0 ? (
                  docsLoading ? (
                    loadingLine
                  ) : (
                    <div className="text-[12px] text-ink/40">
                      {docs.length === 0 ? (
                        <FormattedMessage
                          id="save.extraction.noDocuments"
                          defaultMessage="No documents available."
                        />
                      ) : (
                        // Not "no documents" - the Documents page shows them.
                        <FormattedMessage
                          id="save.extraction.noAnalyzableDocuments"
                          defaultMessage="None of your documents can be analysed."
                        />
                      )}
                    </div>
                  )
                ) : (
                  <div className="max-h-72 space-y-3 overflow-y-auto pr-1">
                    {grouped.map((g) => {
                      const ids = g.docs
                        .filter((d) => isAnalyzableDocument(d.name))
                        .map((d) => d.id);
                      const allSelected = ids.length > 0 && ids.every((id) => selectedIds.has(id));
                      const open = !collapsed.has(g.id);
                      return (
                        <div key={g.id}>
                          <div className="mb-1 flex items-center gap-2 text-[11px] tracking-wider text-ink/45">
                            <button
                              type="button"
                              onClick={() => toggleCollapse(g.id)}
                              aria-label={intl.formatMessage({
                                id: "save.extraction.toggleCollapse",
                                defaultMessage: "Expand/collapse",
                              })}
                              className={`shrink-0 text-ink/40 transition-transform hover:text-ink ${open ? "rotate-90" : ""}`}
                            >
                              <svg
                                viewBox="0 0 24 24"
                                className="h-3.5 w-3.5"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden
                              >
                                <path d="m9 6 6 6-6 6" />
                              </svg>
                            </button>
                            <input
                              type="checkbox"
                              checked={allSelected}
                              disabled={ids.length === 0}
                              onChange={() => toggleMany(ids, !allSelected)}
                              aria-label={intl.formatMessage(
                                {
                                  id: "save.extraction.selectAllInGroup",
                                  defaultMessage: "Select all in {group}",
                                },
                                { group: g.name },
                              )}
                              className="h-3.5 w-3.5 shrink-0 accent-ink"
                            />
                            <button
                              type="button"
                              onClick={() => toggleCollapse(g.id)}
                              className="flex min-w-0 items-center gap-2 tracking-wider"
                            >
                              <span className="truncate">{g.name}</span>
                              <span className="text-ink/30">{g.docs.length}</span>
                            </button>
                          </div>
                          {open ? (
                            <>
                              <div className="space-y-1 pl-4">{g.shown.map(docRow)}</div>
                              <Pager
                                page={g.page}
                                totalPages={g.totalPages}
                                onChange={(next) =>
                                  setFolderPages((p) => ({ ...p, [g.id]: next }))
                                }
                              />
                            </>
                          ) : null}
                        </div>
                      );
                    })}
                    {pagedDocs.length > 0 ? (
                      <div>
                        {grouped.length > 0 ? (
                          <div className="mb-1 text-[11px] tracking-wider text-ink/45">
                            <FormattedMessage
                              id="save.extraction.noFolder"
                              defaultMessage="No folder"
                            />
                          </div>
                        ) : null}
                        <div className="space-y-1">{pagedDocs.map(docRow)}</div>
                      </div>
                    ) : null}
                    {filter && visibleDocs.length === 0
                      ? docsLoading
                        ? loadingLine
                        : (
                            <div className="text-[12px] text-ink/40">
                              <FormattedMessage
                                id="save.extraction.noMatches"
                                defaultMessage="No matches"
                              />
                            </div>
                          )
                      : null}
                  </div>
                )}
                <Pager
                  page={docPageClamped}
                  totalPages={docTotalPages}
                  onChange={setDocPage}
                />
              </div>

              {error ? <div className="text-[11px] text-red-600">{error}</div> : null}
            </>
          )}
        </div>

        {result ? (
          <div className="flex justify-end border-t border-ink/10 px-5 py-3">
            <Button variant="primary" size="compact" onClick={onClose}>
              <FormattedMessage id="save.extraction.done" defaultMessage="Done" />
            </Button>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3 border-t border-ink/10 px-5 py-3">
            <span className="text-[11px] text-ink/55">
              {jobAccepted ? (
                <FormattedMessage
                  id="save.extraction.runningInBackground"
                  defaultMessage="Running in the background - you can close this and keep working. You’ll be notified when it’s done."
                />
              ) : total > 0 ? (
                <FormattedMessage
                  id="save.extraction.selectedCount"
                  defaultMessage="{total, plural, one {# document selected} other {# documents selected}}"
                  values={{ total }}
                />
              ) : (
                <FormattedMessage
                  id="save.extraction.nothingSelected"
                  defaultMessage="Nothing selected"
                />
              )}
            </span>
            <Button
              variant="primary"
              onClick={run}
              disabled={busy || total === 0}
              aria-busy={busy}
              // Fixed min-width so the label swap doesn't change the button
              // size mid-run; the spinner lives INSIDE the pill.
              className="inline-flex min-w-[10rem] items-center justify-center gap-2 px-4 py-1.5 text-[11px] disabled:opacity-40"
            >
              {busy ? (
                <>
                  <span
                    aria-hidden
                    className="h-3 w-3 shrink-0 animate-spin rounded-full border border-page/40 border-t-page"
                  />
                  <FormattedMessage
                    id="save.extraction.analysing"
                    defaultMessage="Analysing …"
                  />
                </>
              ) : (
                <FormattedMessage
                  id="save.extraction.startAnalysis"
                  defaultMessage="Start analysis"
                />
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function ResultView({
  result,
  flags,
  docs,
  newSkills,
  newPowers,
}: {
  result: ProcessResult;
  flags: ExtractionFlags;
  docs: readonly ExtractionDoc[];
  newSkills?: { skill: string; level: string | null }[] | null;
  newPowers?: { element: string; score: number }[] | null;
}) {
  const intl = useIntl();
  const ok = result.status?.toLowerCase() === "ok" || result.status?.toLowerCase() === "success";
  // documentId refs can be mapped from `docs`; inline refs get displayName set
  // from the request order before the result is stored.
  const nameByRef = new Map(docs.map((d) => [d.id, d.name]));
  const line = (label: string, r?: PipelineResult | null) =>
    r ? (
      <div className="box-flat flex items-center justify-between bg-page px-3 py-2 text-[12px]">
        <span className="font-medium">{label}</span>
        <span className="text-ink/55">
          <FormattedMessage
            id="save.extraction.pipelineCounts"
            defaultMessage="{processed} processed · {skipped} skipped · {errored} errors"
            values={{ processed: r.processed, skipped: r.skipped, errored: r.errored }}
          />
        </span>
      </div>
    ) : null;

  return (
    <div className="space-y-3">
      <div className={`text-[13px] font-medium ${ok ? "text-ink" : "text-red-600"}`}>
        {ok ? (
          <FormattedMessage
            id="save.extraction.analysisComplete"
            defaultMessage="Analysis complete."
          />
        ) : (
          <FormattedMessage
            id="save.extraction.statusLine"
            defaultMessage="Status: {status}"
            values={{ status: result.status }}
          />
        )}
      </div>
      {result.message ? <div className="text-[12px] text-ink/55">{result.message}</div> : null}
      <div className="space-y-1.5">
        {flags.extractPowers
          ? line(
              intl.formatMessage({ id: "save.extraction.powers", defaultMessage: "Powers" }),
              result.powers,
            )
          : null}
        {flags.extractSkills
          ? line(
              intl.formatMessage({ id: "save.extraction.skills", defaultMessage: "Skills" }),
              result.skills,
            )
          : null}
      </div>
      {newSkills ? (
        <div>
          <div className="field-label mb-1 text-ink/45">
            <FormattedMessage
              id="save.extraction.newSkillsHeading"
              defaultMessage="Newly detected expertise{count, plural, =0 {} other { (#)}}"
              values={{ count: newSkills.length }}
            />
          </div>
          {newSkills.length === 0 ? (
            <div className="text-[11px] text-ink/45">
              <FormattedMessage
                id="save.extraction.noNewSkills"
                defaultMessage="No new entries - everything was probably already in your expertise."
              />
            </div>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {newSkills.map((s, i) => (
                <span
                  key={`${s.skill}-${i}`}
                  className="box-flat inline-flex items-center gap-1.5 bg-page px-2.5 py-1 text-[11px] text-ink/80"
                >
                  {s.skill}
                  {s.level ? <span className="text-ink/45">· {s.level}</span> : null}
                </span>
              ))}
            </div>
          )}
        </div>
      ) : null}
      {newPowers ? (
        <div>
          <div className="field-label mb-1 text-ink/45">
            <FormattedMessage
              id="save.extraction.newPowersHeading"
              defaultMessage="Newly detected powers{count, plural, =0 {} other { (#)}}"
              values={{ count: newPowers.length }}
            />
          </div>
          {newPowers.length === 0 ? (
            <div className="text-[11px] text-ink/45">
              <FormattedMessage
                id="save.extraction.noNewPowers"
                defaultMessage="No changes - the analysis produced no new power values."
              />
            </div>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {newPowers.map((p, i) => (
                <span
                  key={`${p.element}-${i}`}
                  className="box-flat inline-flex items-center gap-1.5 bg-page px-2.5 py-1 text-[11px] text-ink/80"
                >
                  {intl.formatMessage({
                    id: elementNameId(p.element),
                    defaultMessage: elementDisplayName(p.element),
                  })}
                  <span className="text-ink/45">· {p.score}</span>
                </span>
              ))}
            </div>
          )}
        </div>
      ) : null}
      {result.documents.length > 0 ? (
        <div className="space-y-1.5">
          {result.documents.map((d, i) => {
            const failed = d.status?.toLowerCase() === "error";
            return (
              <div key={`${d.ref}-${i}`} className="text-[11px]">
                <div className="flex items-center justify-between gap-2 text-ink/70">
                  <span className="truncate">{d.displayName ?? nameByRef.get(d.ref) ?? d.ref}</span>
                  <span className={`shrink-0 ${failed ? "text-red-600" : "text-ink/55"}`}>{d.status}</span>
                </div>
                {d.message ? (
                  <div className={`mt-0.5 ${failed ? "text-red-600/80" : "text-ink/45"}`}>{d.message}</div>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}
      {result.warnings && result.warnings.length > 0 ? (
        <div className="rounded-md bg-amber-50 px-3 py-2 text-[11px] text-amber-700 ring-1 ring-amber-200">
          {result.warnings.map((w, i) => (
            <div key={i}>{w}</div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
